/**
 * `GET /me/permissions` — итоговый набор permission-ключей текущей
 * сессии (дефолты роли + точечные grant/revoke от админа).
 *
 * До этого фронт брал права из статичной таблицы `ROLE_DEFAULTS`, а
 * всё, что админ настраивал в разделе «Роли» и в overlay пользователя,
 * сохранялось на бэке и никогда не читалось. Теперь источник правды —
 * бэк, таблица осталась фолбэком.
 *
 * Формат ответа заранее не зафиксирован, поэтому парсим терпимо —
 * поддерживаем все вероятные варианты:
 *   ["menu.home", …]
 *   {keys: [...]} | {permissions: [...]} | {items: [...]} | {data: [...]}
 *   [{key: "menu.home"}, …] | [{name: …}] | [{slug: …}]
 *   {granted: [...], revoked: [...]}  — тогда считаем granted − revoked
 *
 * Ключи, которых нет в нашем реестре, отбрасываем: бэк может завести
 * ключ раньше, чем фронт научится его понимать.
 */

import {PERMISSION_KEYS, type PermissionKey} from '@lib/permissions/keys';

import {http} from '@services/utils';

const KNOWN = new Set<string>(PERMISSION_KEYS);

/**
 * Ключи, которых на бэке пока нет, — выводим из соседних.
 *
 * Сверка реестров (август 2026): все 106 ключей бэка есть у нас, а
 * трёх наших у бэка нет. Без вывода разделы «Налоги» и таб
 * «Дубликаты» стали бы недоступны вообще всем, включая SUPER_ADMIN,
 * потому что итоговый набор теперь приходит с сервера.
 *
 * Убрать, как только бэк заведёт ключи (см. docs/be-tasks).
 */
const DERIVED: ReadonlyArray<{from: PermissionKey; add: PermissionKey}> = [
  // Пункт меню налогов бэк отдаёт — значит доступ к разделу есть.
  {from: 'menu.tax', add: 'finance.tax.view'},
  // Отправка/правка отчёта — то же, что и остальная финансовая работа.
  {from: 'menu.tax', add: 'finance.tax.write'},
  // Дубликаты живут в «Отчётах» рядом со статистикой.
  {from: 'reports.statistic', add: 'reports.duplicate'},
];

const withDerived = (list: PermissionKey[]): PermissionKey[] => {
  const set = new Set(list);
  for (const {from, add} of DERIVED) {
    if (set.has(from)) set.add(add);
  }
  return [...set];
};

/** Из произвольного элемента вытащить строку-ключ. */
const toKeyString = (item: unknown): string | null => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    for (const field of ['key', 'name', 'slug', 'code', 'permission']) {
      const v = o[field];
      if (typeof v === 'string') return v;
    }
  }
  return null;
};

const collect = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(toKeyString).filter((k): k is string => k !== null);
};

/** Достать массив по первому непустому из вероятных полей-обёрток. */
const unwrap = (raw: unknown): unknown => {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  for (const field of ['payload', 'keys', 'permissions', 'items', 'list', 'data']) {
    if (Array.isArray(o[field])) return o[field];
  }
  return [];
};

/**
 * `null` = бэк ответил в непонятном виде или не дал ни одного знакомого
 * ключа. Вызывающий обязан в этом случае откатиться на `ROLE_DEFAULTS`,
 * иначе пользователь останется с пустым интерфейсом.
 */
export const getPermissionKeys = async (): Promise<PermissionKey[] | null> => {
  const raw = await http<unknown>('me/permissions');

  let names: string[];
  const asObject = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null;
  const granted = asObject ? (asObject as Record<string, unknown>).granted : undefined;

  if (Array.isArray(granted)) {
    const revoked = new Set(collect((asObject as Record<string, unknown>).revoked));
    names = collect(granted).filter((k) => !revoked.has(k));
  } else {
    names = collect(unwrap(raw));
  }

  const known = names.filter((k): k is PermissionKey => KNOWN.has(k));

  if (import.meta.env.DEV) {
    const unknown = names.filter((k) => !KNOWN.has(k));
    console.info('[permissions] с бэка:', names.length, '→ знакомых:', known.length, raw);
    if (unknown.length) console.warn('[permissions] нет в реестре фронта:', unknown);
  }

  // Ни одного знакомого ключа — почти наверняка другой контракт.
  // Молча оставить юзера без прав хуже, чем отработать по дефолтам роли.
  if (known.length === 0) {
    console.warn('[permissions] ответ /me/permissions не распознан — работаем по ROLE_DEFAULTS');
    return null;
  }

  return withDerived(known);
};
